import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, Users, ArrowRight, CheckCircle, User, Award } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { studyGroups } from '../data/studyGroups';
import { studyGroupDetails } from '../data/studyGroupDetails';

const StudyGroupDetailPage = () => {
  const { id } = useParams();
  const { theme } = useTheme();
  const group = studyGroups.find(g => g.id === parseInt(id));
  const details = studyGroupDetails[id];

  if (!group || !details) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[var(--text-main)] mb-4">Study Group Not Found</h1>
          <Link to="/services/study-groups" className="text-[var(--accent-cyan)] hover:underline">Back to Study Groups</Link>
        </div>
      </div>
    );
  }

  const handleApply = () => {
    toast.success(`Enrollment started for ${group.title}`, { description: 'Redirecting to enrollment form...' });
  };

  return (
    <div className="min-h-screen pt-20" data-testid="study-group-detail-page">
      <Toaster position="top-right" theme={theme} toastOptions={{ style: { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-main)' } }} />

      {/* Hero */}
      <section className="py-10 md:py-16 relative overflow-hidden border-b border-[var(--border)]">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container-main relative z-10">
          <Link to="/services/study-groups" className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--primary)] mb-6 font-mono text-sm">
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to Study Groups
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <span className={`px-3 py-1 text-xs font-mono uppercase ${group.status === 'upcoming' ? 'bg-[var(--primary)]/20 text-[var(--primary)]' : 'bg-[var(--text-muted)]/20 text-[var(--text-muted)]'}`}>{group.status === 'upcoming' ? 'Coming Soon' : group.status}</span>
                <span className="px-3 py-1 bg-[var(--accent-cyan)]/20 text-[var(--accent-cyan)] text-xs font-mono uppercase">{group.level}</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-main)] mb-6">{group.title}</h1>
              <p className="text-[var(--text-muted)] text-lg leading-relaxed mb-6">{details.summary}</p>
              <div className="flex flex-wrap gap-2">
                {group.tags.map((tag, idx) => (<span key={idx} className="px-3 py-1 bg-[var(--accent-cyan)]/10 border border-[var(--accent-cyan)]/30 text-sm text-[var(--accent-cyan)] font-mono">{tag}</span>))}
              </div>
            </div>
            <div className="bg-[var(--surface)] border border-[var(--border)] p-6">
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3"><Calendar className="w-5 h-5 text-[var(--accent-cyan)]" /><div><p className="text-xs text-[var(--text-muted)]">Starts</p><p className="text-[var(--text-main)] font-mono">{group.startDate}</p></div></div>
                <div className="flex items-center gap-3"><Clock className="w-5 h-5 text-[var(--accent-cyan)]" /><div><p className="text-xs text-[var(--text-muted)]">Schedule</p><p className="text-[var(--text-main)] font-mono">{group.meetingFrequency}</p></div></div>
                <div className="flex items-center gap-3"><Users className="w-5 h-5 text-[var(--accent-cyan)]" /><div><p className="text-xs text-[var(--text-muted)]">Spots Left</p><p className="text-[var(--text-main)] font-mono">{group.spotsLeft}/{group.groupSize}</p></div></div>
              </div>
              <div className="border-t border-[var(--border)] pt-6">
                <p className="text-xs text-[var(--text-muted)] mb-1">Enrollment Fee</p>
                <p className="font-mono text-4xl font-bold text-[var(--accent-cyan)] mb-2">FREE</p>
                <p className="text-xs text-[var(--text-muted)]">Community-driven learning</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mentor */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-cyan)] text-sm uppercase tracking-wider mb-6">./mentor</p>
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-[var(--surface-highlight)] border border-[var(--accent-cyan)] flex items-center justify-center font-mono text-[var(--accent-cyan)] text-2xl font-bold">
              {group.mentor.split(' ').map(n => n[0]).join('')}
            </div>
            <div>
              <h3 className="text-2xl font-bold text-[var(--text-main)]">{group.mentor}</h3>
              <p className="text-[var(--text-muted)]">Study Group Mentor • Community Leader</p>
            </div>
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-cyan)] text-sm uppercase tracking-wider mb-6">./learning_path</p>
          <div className="space-y-4">
            {details.curriculum.map((week, idx) => (
              <div key={idx} className="flex items-center gap-4 p-4 bg-[var(--surface)] border border-[var(--border)]">
                <div className="w-10 h-10 bg-[var(--accent-cyan)]/20 border border-[var(--accent-cyan)] flex items-center justify-center font-mono text-[var(--accent-cyan)] font-bold">{idx + 1}</div>
                <span className="text-[var(--text-main)]">{week}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Topics */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-cyan)] text-sm uppercase tracking-wider mb-6">./topics_covered</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {details.topics.map((topic, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 border border-[var(--border)]">
                <CheckCircle className="w-5 h-5 text-[var(--primary)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-main)]">{topic}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who is this for */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-cyan)] text-sm uppercase tracking-wider mb-6">./who_should_join</p>
          <ul className="space-y-3">
            {details.suitableFor.map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <User className="w-5 h-5 text-[var(--accent-cyan)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-main)]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-10 border-b border-[var(--border)]">
        <div className="container-main">
          <p className="font-mono text-[var(--accent-cyan)] text-sm uppercase tracking-wider mb-6">./benefits</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {details.benefits.map((benefit, idx) => (
              <div key={idx} className="p-4 border border-[var(--border)]">
                <Award className="w-6 h-6 text-[var(--primary)] mb-3" />
                <p className="text-[var(--text-main)]">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join CTA */}
      <section className="py-8 md:py-12 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="container-main relative z-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-main)] mb-4">Ready to Join?</h2>
          <p className="text-[var(--text-muted)] text-lg max-w-2xl mx-auto mb-8">
            Enroll in the {group.title} study group. Only {group.spotsLeft} spots remaining.
          </p>
          <button onClick={handleApply} disabled={group.status === 'archived'} className="bg-[var(--primary)] text-black font-mono font-bold px-12 py-4 text-lg uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_25px_var(--glow-primary)] disabled:opacity-50" data-testid="enroll-btn">
            {group.status !== 'archived' ? 'Enroll Now' : 'Group Closed'}
          </button>
        </div>
      </section>
    </div>
  );
};

export default StudyGroupDetailPage;
